/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import axios from "axios";
import { Auth } from "aws-amplify";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
import { APIGateways } from "../../../utility/APIGateways";

const COMPONENT_NAME = "adminCCPServices/ConnectReports";

const lookupCache = {};
const CACHE_KEY = "lookupData";

const ran = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);

const _fakeData = () => ({
	SERVICE_LEVEL_15: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	SERVICE_LEVEL_180: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	SERVICE_LEVEL_240: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	SERVICE_LEVEL_30: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	SERVICE_LEVEL_120: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	OCCUPANCY: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	SERVICE_LEVEL_300: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	SERVICE_LEVEL_600: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	CONTACTS_MISSED: { value: ran(1, 50), unit: "COUNT", statistic: "SUM" },
	CONTACTS_QUEUED: { value: ran(1, 50), unit: "COUNT", statistic: "SUM" },
	SERVICE_LEVEL_60: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	QUEUE_ANSWER_TIME: { value: ran(1, 500), unit: "SECONDS", statistic: "AVG" },
	AGENTS_AVAILABLE: { value: ran(1, 50), unit: "COUNT" },
	OLDEST_CONTACT_AGE: { value: ran(1, 500), unit: "SECONDS" },
	HANDLE_TIME: { value: ran(1, 500), unit: "SECONDS", statistic: "AVG" },
	SERVICE_LEVEL_25: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	AGENTS_ON_CONTACT: { value: ran(1, 50), unit: "COUNT" },
	CONTACTS_HANDLED: { value: ran(1, 50), unit: "COUNT", statistic: "SUM" },
	SERVICE_LEVEL_20: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	AGENTS_STAFFED: { value: ran(1, 50), unit: "COUNT" },
	SERVICE_LEVEL_45: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	CONTACTS_IN_QUEUE: { value: ran(1, 50), unit: "COUNT" },
	SERVICE_LEVEL_90: { value: ran(1, 50), unit: "PERCENT", statistic: "AVG" },
	CONTACTS_HANDLED_INCOMING: { value: ran(1, 50), unit: "COUNT", statistic: "SUM" },
	QUEUED_TIME: { value: ran(1, 500), unit: "SECONDS", statistic: "MAX" },
});

const _getUserToken = async () => {
	try {
		return (await Auth.currentSession()).getIdToken().getJwtToken();
	} catch (error) {
		return null;
	}
};

export const getConnectReports = async (queueArn) => {
	try {
		if (window.localStorage.getItem("fake-metrics")) {
			return _fakeData();
		}

		const token = await _getUserToken();

		// skip fetching metrics if user is not logged in or
		// auth token expired
		if (!token) {
			v2.warn("User not logged in while fetching metrics", {
				componentName: COMPONENT_NAME,
				logCode: errorCodes.USER_LOGGED_OUT_WARNING,
			});
			return [];
		}

		// skip fetching metrics if user is offline
		if (!navigator.onLine) {
			v2.warn("User offline while fetching metrics", {
				componentName: COMPONENT_NAME,
				logCode: errorCodes.USER_OFFLINE_WARNING,
			});
			return [];
		}

		const response = await axios.get(`${APIGateways.connectReportsEndpoint}?queueArn=${queueArn}`, {
			headers: {
				Authorization: token,
			},
		});

		return response.data;
	} catch (error) {
		v2.warn("Error while getting connect reports", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.CONNECT_REPORTS_GET_API_ERROR,
		});

		return [];
	}
};

export const getConnectReportsLookupData = async () => {
	try {
		if (lookupCache[CACHE_KEY]) {
			return lookupCache[CACHE_KEY];
		}

		const token = await _getUserToken();

		const response = await axios.get(`${APIGateways.connectReportsEndpoint}LookupData`, {
			headers: {
				Authorization: token,
			},
		});

		lookupCache[CACHE_KEY] = response.data;

		return response.data;
	} catch (error) {
		v2.error("Error while getting connect reports lookup data", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.CONNECT_REPORTS_LOOKUPS_GET_API_ERROR,
		});
		return [];
	}
};
